import {
  GRADE_BANDS,
  gradeFor,
  gradeForPaper,
  gradeToneClasses,
  type GradeBand,
} from '@/constants/grading';
import type { StudentExamMark } from '@/types/student';

/**
 * One exam as a family reads it: every paper the child sat under one name
 * ("Unit Test I"), with the totals worked out once.
 *
 * Shared by the results view and the report cards tab, which used to group the
 * papers in one and invent the other — terms, dates and marks nobody had
 * entered. Both now read the same groups, built from the marks the server
 * returned and nothing else.
 */
export interface ExamGroup {
  name: string;
  papers: StudentExamMark[];
  /** `YYYY-MM-DD` of the first and last paper. */
  startDate: string;
  endDate: string;
  /** "01 Oct 2026 – 05 Oct 2026", or one day when the exam was one day. */
  dateRange: string;
  totalObtained: number;
  totalMax: number;
  /** Null when no paper carries a score — absent from all, or none marked yet. */
  percentage: number | null;
  /** Null exactly when `percentage` is: nothing scored is not a low grade. */
  gradeInfo: GradeInfo | null;
}

/** A band of the shared scale, with the classes a family screen draws it in. */
export interface GradeInfo extends GradeBand {
  color: string;
  badgeClass: string;
}

/** A band with the shared tone classes a family screen draws it in. */
function withClasses(band: GradeBand): GradeInfo {
  const tone = gradeToneClasses(band);
  return { ...band, color: tone.text, badgeClass: tone.badge };
}

/**
 * A percentage as a grade, from the one scale the profile screen grades on.
 *
 * This file used to carry its own A+…D bands with different cut-offs, so the
 * same 78% was an "A" on the profile and a "B+" here (CLAUDE.md §3). Null when
 * there is no percentage — the caller shows a dash, never a grade.
 */
export function calculateGrade(pct: number | null): GradeInfo | null {
  const band = gradeFor(pct, 100);
  return band ? withClasses(band) : null;
}

/**
 * One paper's grade, held to that paper's own pass mark.
 *
 * Not `calculateGrade`: a paper that sets its pass at 40 would otherwise show a
 * passing "D" on a failed 35/100 (CLAUDE.md §3 — `gradeForPaper` is the one
 * rule). Null for an absent or unscored paper; the caller writes "AB" or a dash.
 */
export function paperGrade(paper: StudentExamMark): GradeInfo | null {
  if (paper.isAbsent) return null;
  const band = gradeForPaper(paper.marksObtained, paper.maxMarks, paper.passMarks);
  return band ? withClasses(band) : null;
}

/**
 * Whether a grade is worth congratulating. B and above, the bands the shared
 * scale marks as success or info — below that the screen says nothing rather
 * than cheer a result the child did not get.
 */
export function isPraiseworthy(grade: GradeInfo | null): boolean {
  return grade?.tone === 'success' || grade?.tone === 'info';
}

/** The shared scale as "floor – ceiling" rows, highest first, for a Grade Scale card. */
export function gradeScale(): { band: GradeInfo; range: string }[] {
  return GRADE_BANDS.map((band, index) => {
    const ceiling = index === 0 ? 100 : GRADE_BANDS[index - 1].floor - 1;
    return { band: withClasses(band), range: `${band.floor} – ${ceiling}` };
  });
}

/** `2026-10-01` → `01 Oct 2026`, read as a calendar day so no zone can move it. */
export function formatExamDay(dateIso: string): string {
  const [year, month, day] = dateIso.split('-').map(Number);
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/** A percentage of what was sat. Absent papers and unscored ones count for neither side. */
export function percentageOf(papers: StudentExamMark[]): {
  totalObtained: number;
  totalMax: number;
  percentage: number | null;
} {
  const sat = papers.filter((paper) => !paper.isAbsent && paper.marksObtained !== null);
  const totalObtained = sat.reduce((sum, paper) => sum + (paper.marksObtained ?? 0), 0);
  const totalMax = sat.reduce((sum, paper) => sum + paper.maxMarks, 0);

  return {
    totalObtained,
    totalMax,
    // Null rather than 0 when nothing was scored: a missed exam is not a zero.
    percentage: totalMax > 0 ? Math.round((totalObtained / totalMax) * 100) : null,
  };
}

/** Papers grouped by exam name, the most recent exam first. */
export function groupExams(papers: StudentExamMark[]): ExamGroup[] {
  const byName = new Map<string, StudentExamMark[]>();
  for (const paper of papers) {
    byName.set(paper.name, [...(byName.get(paper.name) ?? []), paper]);
  }

  return Array.from(byName.entries())
    .map(([name, group]) => {
      const dates = group.map((paper) => paper.examDate).sort();
      const startDate = dates[0];
      const endDate = dates[dates.length - 1];
      const totals = percentageOf(group);

      return {
        name,
        papers: group,
        startDate,
        endDate,
        dateRange:
          startDate === endDate
            ? formatExamDay(startDate)
            : `${formatExamDay(startDate)} – ${formatExamDay(endDate)}`,
        ...totals,
        gradeInfo: calculateGrade(totals.percentage),
      };
    })
    .sort((a, b) => b.endDate.localeCompare(a.endDate));
}

/**
 * What the teachers wrote against this exam's papers, by subject. Empty when
 * nobody did. `author` is whoever entered that mark, or null when nobody is
 * recorded — the remark is then shown unsigned rather than credited to a guess.
 */
export function remarksOf(
  group: ExamGroup | null,
): { subject: string; remark: string; author: string | null }[] {
  if (!group) return [];

  return group.papers.flatMap((paper) => {
    const remark = paper.remarks?.trim();
    return remark ? [{ subject: paper.subject.name, remark, author: paper.gradedBy }] : [];
  });
}
